import { useFeatureFlagVariantKey } from 'posthog-js/react';

import {
  AUTO_LESSON_ANALYSIS_CHECKOUT_FEATURE_FLAG,
  AUTO_LESSON_ANALYSIS_COURSE_PAGE_FEATURE_FLAG,
} from '../constants/autoAnalysis';
import { isAiBuddy } from '../helper';

export type TAutoLessonAnalysisVisibility = {
  /**
   * Whether the switch renders. Checkout must also use this — and never the
   * raw flag — to decide if the add-on is charged and if `autoAnalysis` goes
   * into the payload, otherwise an opted-in student sees a priced switch whose
   * value never reaches the backend.
   */
  isEnabled: boolean;
  /** The surface's flag resolved to `test` for this user. */
  isFlagEnabled: boolean;
  /**
   * Visible only because the student had already opted in — the flag says
   * hide. Stamped on every event so these users can be filtered out of the
   * experiment, where they would otherwise show up as a `control` user who
   * somehow saw the switch.
   */
  shownByExistingOptIn: boolean;
};

/**
 * Shared gate behind both surfaces.
 *
 * The AI Buddy tenant never gets the feature, whatever the flag resolves to
 * and whatever the enrolment has saved. No auto analysis event fires for those
 * users either, so they stay out of the experiment's funnels.
 *
 * `alreadyEnabled` is the enrolment's saved `autoAnalysisEnabled`. A student
 * who has opted in keeps both switches even when the flag would hide them:
 * on the course page so they can always turn the feature back off, and at
 * checkout because the backend goes on deducting analysis minutes from their
 * credit — hiding the switch there would leave those minutes unfunded and eat
 * into the lessons they just paid for.
 */
const useAutoLessonAnalysisVisibility = (
  flagKey: string,
  alreadyEnabled: boolean
): TAutoLessonAnalysisVisibility => {
  const variant = useFeatureFlagVariantKey(flagKey);

  if (isAiBuddy()) {
    return {
      isEnabled: false,
      isFlagEnabled: false,
      shownByExistingOptIn: false,
    };
  }

  const isFlagEnabled = variant === 'test';

  return {
    isEnabled: isFlagEnabled || alreadyEnabled,
    isFlagEnabled,
    shownByExistingOptIn: !isFlagEnabled && alreadyEnabled,
  };
};

/**
 * Gates the course-page switch. The trial check lives in the card itself and
 * is deliberately not part of this — it is an eligibility rule, not a rollout
 * one, so an opted-in trial enrolment stays hidden as before.
 */
export const useAutoLessonAnalysisCoursePage = (
  alreadyEnabled: boolean
): TAutoLessonAnalysisVisibility =>
  useAutoLessonAnalysisVisibility(
    AUTO_LESSON_ANALYSIS_COURSE_PAGE_FEATURE_FLAG,
    alreadyEnabled
  );

/** Gates both checkout switches — one-time top-up and monthly plans. */
export const useAutoLessonAnalysisCheckout = (
  alreadyEnabled: boolean
): TAutoLessonAnalysisVisibility =>
  useAutoLessonAnalysisVisibility(
    AUTO_LESSON_ANALYSIS_CHECKOUT_FEATURE_FLAG,
    alreadyEnabled
  );

export default useAutoLessonAnalysisCoursePage;
